import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api } from '../../lib/api';
import type { ErpConfig } from '../../lib/types';
import { type ErpForm, MEDICAL_DEFAULTS, MedicalErpFields } from './MedicalErpFields';
import { SectionShell } from './SectionShell';
import type { SectionStatus, TenantSectionProps } from './types';

/**
 * Solo se ofrecen las integraciones que existen de verdad.
 *
 * Se quitaron 'laravel' (0 tenants, era V1) y 'webhook', que era una trampa: el
 * factory hace `throw new Error('WebhookErpAdapter not implemented yet')`, así
 * que el wizard permitía crear y activar un tenant cuyo bot reventaba al primer
 * mensaje. Los literales siguen en TenantConfigSchema por compatibilidad.
 */
export type ErpType = 'medical-ese-sf' | 'mock';

export interface ErpValue {
  type?: string;
  config?: ErpForm;
}

export function erpStatus(value: ErpValue | undefined): SectionStatus {
  if (!value?.type) return 'empty';
  if (value.type === 'medical-ese-sf' && !value.config?.baseUrl) return 'partial';
  return 'complete';
}

export function ErpSection({
  mode,
  tenantId,
  value,
  onSaved,
  testId,
}: TenantSectionProps<ErpValue>) {
  const queryClient = useQueryClient();
  // Un borrador viejo puede traer un tipo que ya no se ofrece ('laravel' /
  // 'webhook'): se degrada a 'mock' en vez de romper el render con un valor
  // que el <select> no tiene.
  const [erpType, setErpType] = useState<ErpType>(() =>
    value?.type === 'medical-ese-sf' || value?.type === 'mock' ? value.type : 'mock',
  );
  const [config, setConfig] = useState<ErpForm>(value?.config ?? {});
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  function buildBody(): ErpConfig | null {
    if (erpType === 'mock') return { type: 'mock' };
    if (!config.baseUrl?.trim()) {
      setError('La URL del servicio es requerida.');
      return null;
    }
    const slot = Number.parseInt(config.slotDurationMinutes ?? '', 10);
    const days = Number.parseInt(config.businessDaysAhead ?? '', 10);
    const cooldown = Number.parseInt(config.cooldownDays ?? '', 10);
    const timeout = Number.parseInt(config.timeoutMs ?? '', 10);
    const slotsPerDay = Number.parseInt(config.slotsPerDayLimit ?? '', 10);
    const apiVersionRaw = (config.apiVersion ?? MEDICAL_DEFAULTS.apiVersion ?? 'v2').trim();
    const apiVersion: 'v1' | 'v2' = apiVersionRaw === 'v1' ? 'v1' : 'v2';
    // allowedSpecialityIds: CSV en el input → array de ints. Vacío → undefined.
    const allowedIdsCsv = (config.allowedSpecialityIds ?? '').trim();
    let allowedSpecialityIds: number[] | undefined;
    if (allowedIdsCsv) {
      const parsed = allowedIdsCsv
        .split(',')
        .map((s) => Number.parseInt(s.trim(), 10))
        .filter((n) => Number.isFinite(n));
      if (parsed.length === 0) {
        setError(
          'Las especialidades permitidas deben ser números separados por coma (ej: 12, 34).',
        );
        return null;
      }
      allowedSpecialityIds = parsed;
    }
    return {
      type: 'medical-ese-sf',
      baseUrl: config.baseUrl.trim(),
      tokenSecretArn: config.tokenSecretArn?.trim() || undefined,
      speciality: config.speciality?.trim() || MEDICAL_DEFAULTS.speciality,
      slotDurationMinutes: Number.isFinite(slot) && slot > 0 ? slot : 20,
      businessDaysAhead: Number.isFinite(days) && days > 0 ? days : 3,
      cooldownDays: Number.isFinite(cooldown) && cooldown >= 0 ? cooldown : 7,
      timeoutMs: Number.isFinite(timeout) && timeout > 0 ? timeout : 8000,
      apiVersion,
      slotsPerDayLimit: Number.isFinite(slotsPerDay) && slotsPerDay > 0 ? slotsPerDay : 6,
      ...(allowedSpecialityIds ? { allowedSpecialityIds } : {}),
    };
  }

  async function save() {
    setError(null);
    const body = buildBody();
    if (!body) return;
    setSaving(true);
    try {
      await api.tenants.setErp(tenantId, body);
      queryClient.invalidateQueries({ queryKey: ['onboarding-status', tenantId] });
      setSaved(true);
      onSaved?.({ type: erpType, config });
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSaving(false);
    }
  }

  function onChangeErpType(next: ErpType) {
    setErpType(next);
    setError(null);
    setSaved(false);
    if (next === 'medical-ese-sf') setConfig((prev) => ({ ...MEDICAL_DEFAULTS, ...prev }));
  }

  // El chip refleja lo guardado, no lo tipeado: decir "Completo" antes de
  // guardar sería mentirle al operador.
  return (
    <SectionShell
      mode={mode}
      testId={testId}
      title="Integración"
      description="El sistema del cliente donde viven las citas."
      status={saved ? erpStatus({ type: erpType, config }) : erpStatus(value)}
      error={error}
      saving={saving}
      saved={saved}
      onSave={save}
    >
      <div className="form-group">
        <label htmlFor="erp-type" className="input-label">
          Tipo de integración
        </label>
        <select
          id="erp-type"
          data-testid="erp-type-select"
          className="input input-md w-full"
          value={erpType}
          onChange={(e) => onChangeErpType(e.target.value as ErpType)}
        >
          <option value="medical-ese-sf">Agendamiento médico (SinuSalud)</option>
          <option value="mock">Demostración (datos de ejemplo)</option>
        </select>
      </div>

      {mode === 'edit' && (
        <div className="toast toast-warning">
          <div className="toast-description">
            La API todavía no devuelve la integración guardada, así que el formulario arranca con
            los valores por defecto. <strong>Guardar reemplaza la integración completa.</strong>
          </div>
        </div>
      )}

      {erpType === 'medical-ese-sf' && <MedicalErpFields config={config} onChange={setConfig} />}

      {erpType === 'mock' && (
        <div data-testid="erp-form-mock" className="toast toast-warning">
          <div className="toast-description">
            <span className="font-semibold block mb-1">⚠️ Datos de demostración</span>
            Es para desarrollo y testing solamente. NO usar en producción: devuelve datos sintéticos
            sin conectar a ningún sistema real.
          </div>
        </div>
      )}
    </SectionShell>
  );
}
